/**
 * Legal Notices Screen
 * Mentions Legales (LCEN, loi 2004-575 art. 6-III)
 * URL: https://wealfi.co/fr/legal
 */

import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  useWindowDimensions,
  Platform,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import useDomain from '../../hooks/useDomain';
import useWebSEO from '../../hooks/useWebSEO';

const brandColors = {
  green: '#71B352',
  greenDark: '#5B8A3C',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
  gray: '#6B7280',
  background: '#FAFBFC',
  white: '#FFFFFF',
};

export default function LegalNoticesScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { isLandingDomain } = useDomain();

  useWebSEO({
    title: 'Mentions Légales — Wealfi',
    description: 'Mentions légales de Wealfi. Éditeur, hébergeur, statut réglementaire (non CIF/CGP/PSI), connexion bancaire Powens ACPR n°16948.',
    keywords: 'mentions légales Wealfi, statut réglementaire, ACPR Powens, éditeur',
    ogUrl: 'https://wealfi.co/fr/legal',
  });

  const handleGoBack = () => {
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = '/fr';
      return;
    }
    navigation.navigate('Landing' as any);
  };

  const handleNavigateToPrivacy = () => {
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = '/fr/privacy';
      return;
    }
    navigation.navigate('fr/privacy' as any);
  };

  return (
    <View style={styles.container}>
      {/* Fixed Navbar */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <Pressable onPress={handleGoBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Retour</Text>
        </Pressable>
        <View style={styles.logoContainer}>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </View>
        <View style={{ width: 80 }} />
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.content, isDesktop && { maxWidth: 800, alignSelf: 'center', width: '100%' }]}>
          {/* Page Title */}
          <Text style={styles.pageTitle}>Mentions Légales</Text>
          <Text style={styles.updateDate}>Dernière mise à jour : 24 février 2026</Text>

          {/* Section 1 - Editeur du site */}
          <View style={styles.section}>
            <Text style={styles.heading}>1. Éditeur du site</Text>
            <Text style={styles.paragraph}>
              Le présent site et l’application Wealfi sont édités par :
            </Text>
            <Text style={styles.paragraph}>
              Boubacar Diallo{'\n'}
              Entrepreneur individuel{'\n'}
              Email : contact@wealfi.co
            </Text>
            <Text style={styles.paragraph}>
              Note : Wealfi est en cours de constitution en société (SAS). Les présentes mentions légales seront mises à jour lors de l’immatriculation au Registre du Commerce et des Sociétés.
            </Text>
          </View>

          {/* Section 2 - Hebergeur */}
          <View style={styles.section}>
            <Text style={styles.heading}>2. Hébergeur</Text>
            <Text style={styles.paragraph}>
              L’application est hébergée par :
            </Text>
            <Text style={styles.paragraph}>
              [À compléter — nom, raison sociale, adresse et coordonnées de l’hébergeur seront renseignés prochainement]
            </Text>
          </View>

          {/* Section 3 - Statut reglementaire (warning style) */}
          <View style={styles.section}>
            <Text style={styles.heading}>3. Statut réglementaire</Text>
            <View style={styles.warningSection}>
              <Text style={styles.paragraph}>
                Wealfi est un service d’assistance financière basé sur l’intelligence artificielle.
              </Text>
              <Text style={[styles.paragraph, { fontWeight: '700' }]}>
                Wealfi n’est PAS :
              </Text>
              <Text style={styles.bulletItem}>
                • Un établissement de crédit au sens du Code monétaire et financier
              </Text>
              <Text style={styles.bulletItem}>
                • Un Prestataire de Services d’Investissement (PSI) agréé par l’ACPR
              </Text>
              <Text style={styles.bulletItem}>
                • Un Conseiller en Investissements Financiers (CIF) enregistré auprès de l’AMF
              </Text>
              <Text style={styles.bulletItem}>
                • Un Conseiller en Gestion de Patrimoine (CGP)
              </Text>
              <Text style={styles.bulletItem}>
                • Un intermédiaire en assurance (IAS)
              </Text>
              <Text style={[styles.paragraph, { marginTop: 12 }]}>
                Les analyses et suggestions fournies par Wealfi sont à caractère éducatif et informatif uniquement. Elles ne constituent pas un conseil en investissement au sens de la directive MiFID II.
              </Text>
              <Text style={[styles.paragraph, { marginTop: 12 }]}>
                Connexion bancaire : cette fonctionnalité optionnelle est assurée par Powens SAS, établissement de paiement agréé par l’Autorité de Contrôle Prudentiel et de Résolution (ACPR) sous le numéro 16948. Wealfi n’accède pas directement aux systèmes bancaires et ne stocke pas les identifiants de connexion.
              </Text>
            </View>
          </View>

          {/* Section 4 - Propriete intellectuelle */}
          <View style={styles.section}>
            <Text style={styles.heading}>4. Propriété intellectuelle</Text>
            <Text style={styles.paragraph}>
              L’ensemble des éléments composant le site et l’application Wealfi (textes, images, graphiques, logo, icônes, logiciels, base de données) sont protégés par les lois françaises et internationales relatives à la propriété intellectuelle.
            </Text>
            <Text style={styles.paragraph}>
              Toute reproduction, représentation, modification, publication, transmission ou dénaturation, totale ou partielle, du site ou de son contenu, par quelque procédé que ce soit, et sur quelque support que ce soit, est interdite sans l’autorisation écrite préalable de Wealfi.
            </Text>
            <Text style={styles.paragraph}>
              La marque « Wealfi » et le logo associé sont la propriété de Boubacar Diallo. Toute utilisation non autorisée est constitutive de contrefaçon.
            </Text>
          </View>

          {/* Section 5 - Donnees personnelles */}
          <View style={styles.section}>
            <Text style={styles.heading}>5. Données personnelles</Text>
            <Text style={styles.paragraph}>
              Wealfi collecte et traite des données personnelles dans le cadre de la fourniture de ses services. Pour connaître l’ensemble de nos engagements en matière de protection des données personnelles, consulte notre Politique de Confidentialité.
            </Text>
            <Pressable onPress={handleNavigateToPrivacy}>
              <Text style={styles.link}>
                Consulter notre Politique de Confidentialité
              </Text>
            </Pressable>
            <Text style={[styles.paragraph, { marginTop: 12 }]}>
              Conformément au Règlement Général sur la Protection des Données (RGPD), tu disposes de droits sur tes données. Pour les exercer, contacte-nous à : contact@wealfi.co
            </Text>
          </View>

          {/* Section 6 - Mediation de la consommation */}
          <View style={styles.section}>
            <Text style={styles.heading}>6. Médiation de la consommation</Text>
            <Text style={styles.paragraph}>
              Conformément aux articles L611-1 et R612-1 du Code de la consommation, tout consommateur a le droit de recourir gratuitement à un médiateur de la consommation en vue de la résolution amiable d’un litige.
            </Text>
            <Text style={styles.paragraph}>
              Le médiateur de la consommation compétent sera désigné et communiqué ultérieurement sur cette page. En attendant, pour toute réclamation, tu peux nous contacter à : contact@wealfi.co
            </Text>
          </View>

          {/* Section 7 - Contact */}
          <View style={styles.section}>
            <Text style={styles.heading}>7. Contact</Text>
            <Text style={styles.paragraph}>
              Pour toute question concernant le site, l’application ou les présentes mentions légales :
            </Text>
            <View style={styles.contactBox}>
              <Text style={styles.paragraph}>
                Email : contact@wealfi.co{'\n'}
                Site web : https://wealfi.co
              </Text>
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable onPress={handleGoBack}>
              <Text style={styles.footerLink}>← Retour à l’accueil</Text>
            </Pressable>
            <Text style={styles.footerCopyright}>
              © 2026 Wealfi. Tous droits réservés.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: brandColors.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70,
  },
  content: {
    paddingHorizontal: 20,
    paddingBottom: 40,
  },

  // Navbar
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  backButton: {
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  backButtonText: {
    fontSize: 15,
    fontWeight: '500',
    color: brandColors.green,
  },
  logoContainer: {
    alignItems: 'center',
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.green,
  },

  // Page Header
  pageTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: brandColors.dark,
    marginTop: 32,
    marginBottom: 8,
  },
  updateDate: {
    fontSize: 14,
    color: brandColors.gray,
    marginBottom: 32,
  },

  // Sections
  section: {
    marginBottom: 28,
  },
  heading: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 15,
    color: brandColors.dark,
    lineHeight: 24,
    marginBottom: 8,
  },
  bulletItem: {
    fontSize: 15,
    color: brandColors.dark,
    lineHeight: 24,
    paddingLeft: 16,
    marginBottom: 4,
  },

  // Warning Section (Section 3)
  warningSection: {
    backgroundColor: brandColors.orangeLight,
    borderLeftWidth: 4,
    borderLeftColor: brandColors.orange,
    borderRadius: 8,
    padding: 16,
  },

  // Footer
  footer: {
    marginTop: 32,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    alignItems: 'center',
    gap: 12,
  },
  footerLink: {
    fontSize: 15,
    fontWeight: '500',
    color: brandColors.green,
  },
  footerCopyright: {
    fontSize: 13,
    color: brandColors.gray,
  },

  // Contact Box
  contactBox: {
    backgroundColor: brandColors.greenLight,
    borderRadius: 12,
    padding: 16,
    marginTop: 8,
  },

  // Link
  link: {
    fontSize: 15,
    color: brandColors.green,
    textDecorationLine: 'underline',
    marginTop: 8,
    marginBottom: 4,
  },
});
